import axios from "axios";
import {
  clearSession,
  getSession,
  normalizeAuthResponse,
  saveSession,
} from "@/lib/auth-session";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/tyretrack`,
});

let refreshPromise = null;
let onSessionInvalidated = null;

export const setSessionInvalidatedHandler = (handler) => {
  onSessionInvalidated = handler;
};

const invalidateSession = () => {
  clearSession();
  onSessionInvalidated?.();
};

const refreshSession = async () => {
  const session = getSession();
  if (!session) {
    throw new Error("Sessione non disponibile");
  }

  const response = await api.post(
    "/auth/refresh",
    {
      access_token: session.accessToken,
      refresh_token: session.refreshToken,
    },
    { skipAuth: true },
  );
  const refreshedSession = normalizeAuthResponse(response.data);
  saveSession(refreshedSession);
  return refreshedSession;
};

api.interceptors.request.use((config) => {
  if (!config.skipAuth) {
    const session = getSession();
    if (session?.accessToken) {
      config.headers.Authorization = `Bearer ${session.accessToken}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isUnauthorized = response?.status === 401;
    const isRefreshRequest = config?.url === "/auth/refresh";

    if (!isUnauthorized || config?.skipAuth || config?._retried || isRefreshRequest) {
      return Promise.reject(error);
    }

    config._retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = refreshSession();
      }
      const session = await refreshPromise;
      config.headers.Authorization = `Bearer ${session.accessToken}`;
      return api(config);
    } catch (refreshError) {
      invalidateSession();
      return Promise.reject(refreshError);
    } finally {
      refreshPromise = null;
    }
  },
);

export default api;
